import { inject, Injectable } from '@angular/core';
// @ts-expect-error No type declaration type exist
import { stringify as wktStringify } from 'wkt';

import { GeographicFilterService } from '@connect-services/client/services/geographic-filter.service';
import { MapDrawService } from '@connect-services/client/services/map-draw.service';
import { SelectZoneLayerService } from '@connect-services/client/services/select-zone-layer.service';
import { MultiPolygon, Polygon } from '@connect-services/src/sdk/api';

type SelectedGeometry = Polygon | MultiPolygon;

@Injectable({
    providedIn: 'root',
})
export class ZoneDelimiterService {
    private readonly geographicFilterService = inject(GeographicFilterService);
    private readonly mapDrawService = inject(MapDrawService);
    private readonly selectZoneLayerService = inject(SelectZoneLayerService);

    public getZoneDelimiter(): string | undefined {
        const delimiterFromSelected: string | undefined = this.getZoneDelimiterFromSelectedGeometries(
            this.selectZoneLayerService.selectedGeometries,
        );

        if (delimiterFromSelected) {
            return delimiterFromSelected;
        }

        return this.getZoneDelimiterFromDrawPolygons();
    }

    public async getZoneDelimiterForSelection(): Promise<string | undefined> {
        const zoneDelimiter: string | undefined = this.getZoneDelimiter();

        if (!zoneDelimiter && this.geographicFilterService.selectedExtentLayersAndEntities()?.layers.length) {
            await this.selectZoneLayerService.selectAllPolygonEntitiesFromSelectedSources();

            return this.getZoneDelimiter();
        }

        return zoneDelimiter;
    }

    public getZoneDelimiterFromDrawPolygons(): string | undefined {
        if (!Object.keys(this.mapDrawService.currentPolygons).length) {
            return;
        }

        const multiPolygon: MultiPolygon = {
            coordinates: [[[]]],
            type: MultiPolygon.TypeEnum.MULTI_POLYGON,
        };

        multiPolygon.coordinates = Object.values(this.mapDrawService.currentPolygons).map((polygon: Polygon) => {
            return [polygon.coordinates[0]];
        });

        return wktStringify(multiPolygon);
    }

    public getZoneDelimiterFromSelectedGeometries(
        selectedGeometries: Record<string, SelectedGeometry>,
    ): string | undefined {
        const geometries = Object.values(selectedGeometries);

        if (!geometries.length) {
            return;
        }

        const coordinates = geometries.reduce((acc: number[][][][], geometry: SelectedGeometry): number[][][][] => {
            if (geometry.type === Polygon.TypeEnum.POLYGON) {
                return [...acc, geometry.coordinates];
            }
            if (geometry.type === MultiPolygon.TypeEnum.MULTI_POLYGON) {
                return [...acc, ...geometry.coordinates];
            }

            return acc;
        }, []);
        if (!coordinates.length) {
            return;
        }

        return wktStringify({
            coordinates,
            type: MultiPolygon.TypeEnum.MULTI_POLYGON,
        });
    }

    public async removeGeographicFilter(): Promise<void> {
        this.mapDrawService.removeOldPolygons();
        await this.selectZoneLayerService.removeLayers();
    }

    public async resetFilters(): Promise<void> {
        await this.removeGeographicFilter();
        this.resetZoneDelimiterData();
    }

    public resetZoneDelimiterData(): void {
        this.geographicFilterService.selectedExtentLayersAndEntities.set(undefined);
        this.mapDrawService.drawPolygonByLayerId = {};
        this.mapDrawService.currentPolygons = {};
        this.selectZoneLayerService.selectedGeometries = {};
    }

    public restoreZoneDelimited(): void {
        const zoneDelimiter: string | undefined = this.getZoneDelimiter();

        if (!zoneDelimiter) {
            return;
        }

        if (!this.mapDrawService.hasDrawnPolygon()) {
            this.selectZoneLayerService.restoreSelectedGeometriesOnMap();

            return;
        }

        this.mapDrawService.restorePolygonsOnMap();
    }
}
